// Turns POST /scanner/lookup's answer into the card the Lookup tool shows. Reads the
// existing labels (itemLabel, the part page's status reading, the asset status words)
// rather than keeping a second copy of any of them.

import { itemLabel } from '../../components/ItemName';
import { statusReading } from '../../Parts/PartInstancePage';
import { STATUS_LABELS, type OperationalStatus } from '../../Assets/types';
import type { ScanLookupResult } from '../../utils/scanDestination';
import type { StickerReading } from './types';

const tail = (uid: string) => uid.replace(/:/g, '').slice(-6).toUpperCase();
const fitted = (uid: string | null | undefined) => (uid ? `Fitted, ends ${tail(uid)}` : 'None fitted');

/** null when the answer names nothing a card can show. */
export function describeSticker({ kind, payload }: ScanLookupResult): StickerReading | null {
  if (!payload) return null;
  switch (kind) {
    case 'drone':
      if (!payload.serialnumber) return null;
      return {
        kind: 'Drone',
        name: payload.serialnumber,
        nameIsCode: true,
        subtitle: payload.title || null,
        facts: [
          ...(payload.condition ? [{ label: 'Condition', value: String(payload.condition) }] : []),
          { label: 'Autopilot', value: fitted(payload.fcid) },
          { label: 'ESC', value: fitted(payload.escid) },
        ],
        warning: null,
      };
    case 'board': {
      if (!payload.primary_uid) return null;
      const mismatch = payload.label_mismatch;
      return {
        kind: payload.board_type ? `${payload.board_type} board` : 'Board',
        name: payload.primary_uid,
        nameIsCode: true,
        subtitle: null,
        facts: [{ label: 'Drone', value: payload.serialnumber || 'Not in a drone' }],
        warning: mismatch
          ? `Sticker says ${mismatch.sticker_prefix}, this board is ${mismatch.board_type}. Check the label is on the right hardware.`
          : null,
      };
    }
    case 'part_instance':
      if (!payload.id) return null;
      return {
        kind: 'Part',
        name: itemLabel(payload),
        nameIsCode: false,
        subtitle: payload.sku && payload.display_name ? payload.sku : null,
        facts: [
          { label: 'Status', value: statusReading(payload, null) },
          ...(payload.serial ? [{ label: 'Serial', value: String(payload.serial) }] : []),
        ],
        warning: null,
      };
    case 'asset':
      if (payload.id == null) return null;
      return {
        kind: payload.asset_type?.label || 'Asset',
        name: payload.display_code || payload.barcode || String(payload.id),
        nameIsCode: true,
        subtitle: null,
        facts: [
          ...(payload.operational_status
            ? [{
                label: 'Status',
                value: STATUS_LABELS[payload.operational_status as OperationalStatus] ?? String(payload.operational_status),
              }]
            : []),
          ...(payload.location_bucket ? [{ label: 'Where', value: String(payload.location_bucket) }] : []),
        ],
        warning: null,
      };
    default:
      return null;
  }
}
